import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbChartTheme, adbRedrawOnThemeChange} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  adbUsageTrendEmpty,
  adbUsageTrendNotes,
  adbUsageTrendOption,
  adbUsageWeekTitle
} from '@features/admin-panel/helpers/admin-dashboard-usage-chart';
import {AdminDashboardQueryParams, AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardUsageTrend} from '@features/admin-panel/models/admin-dashboard-usage.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const entitiesTrendCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/**
 * "Projects, scenarios & entities" → "Entities" (ITP-E-214-04, Plan ITP-P-50 story 10): the in-scope total of entities
 * per week from the nightly snapshot as a line, the latest week partial, the 12-week forecast with its 80 % band, the
 * total, and the notes of story 12. Reloads whenever the dashboard's query params change (the query ignores the period).
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-entities-trend',
  templateUrl: './admin-dashboard-entities-trend.component.html',
  styleUrls: ['./admin-dashboard-entities-trend.component.scss']
})
export class AdminDashboardEntitiesTrendComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly cardTitle = 'Entities';
  readonly description = 'Total entities per week, with a 12-week forecast.';
  readonly emptyTitle = 'No entities in this view';
  readonly failedTitle = 'Couldn\'t load entities';

  data: AdminDashboardUsageTrend | null = null;
  /** The notes under the chart (story 12), set with each response. */
  notes: string[] = [];
  /** False until the first response, so the empty state never flashes. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('platform-usage', this.host.nativeElement, this);
  }

  /** The chart container exists only while there is something to draw. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = entitiesTrendCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** No response, or no entity in any week while the total is unknown or 0. */
  get isEmpty(): boolean {
    return adbUsageTrendEmpty(this.data);
  }

  /** "1,210 entities"; '' without a total. */
  get numbersText(): string {
    const total = this.data?.total;
    return total === null || total === undefined ? '' : countOf(total, 'entity', 'entities');
  }

  /** "Week of 28 Sep: 1,200 entities"; "Week of 5 Oct, latest: 1,210 entities" for the current week. */
  tooltipText(index: number): string {
    const week = this.data?.weeks?.[index];
    return week ? `${adbUsageWeekTitle(week, 'latest')}: ${countOf(week.value, 'entity', 'entities')}` : '';
  }

  /** The card for the PDF report, built from what it shows now; never reloads. */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: this.cardTitle, text: this.failedTitle}];
    }
    if (this.isEmpty) {
      return [{kind: 'empty', title: this.cardTitle, text: this.emptyTitle}];
    }
    const block: AdminDashboardReportBlock = {
      kind: 'chart',
      title: this.cardTitle,
      subtitle: this.numbersText || this.description,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    };
    if (this.notes.length) {
      block.note = this.notes.join(' · ');
    }
    return [block];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardUsageTrend>(
      'AdminDashboardEntitiesTrendQuery', this.params, 'Trend'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(data: AdminDashboardUsageTrend | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.notes = adbUsageTrendNotes(data);
    this.render();
    this.report(failed ? 'failed' : this.isEmpty ? 'empty' : 'content');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.data) {
      return;
    }
    // Colours come from the card's --adb-* tokens: the line in green, the forecast in ink 2 (adbUsageTrendOption).
    const t = adbChartTheme(this.host.nativeElement);
    const weeks = this.data.weeks ?? [];
    this.chart.setOption(adbUsageTrendOption(t, {
      weeks,
      forecast: this.data.forecast,
      kind: 'line',
      layers: [{name: 'Entities', color: t.green, values: weeks.map(week => week.value)}],
      weekTooltip: index => this.tooltipText(index)
    }), true);
  }
}
